import { useState, useEffect } from 'react';
import { supabase, isSupabaseAvailable } from '../lib/supabase';
import { useTenant } from './useTenant';

// Default branding configuration for admin and fallback
const defaultCemeteryBranding: CemeteryBranding = {
  primary_color: '#1f2937',
  primary_color_hover: '#111827',
  secondary_color_1: '#374151',
  secondary_color_2: '#4b5563',
  secondary_color_3: '#6b7280',
  secondary_color_4: '#9ca3af',
  secondary_color_5: '#d1d5db',
  secondary_color_6: '#e5e7eb',
  accent_color: '#3b82f6',
  background_color_primary: '#ffffff',
  background_color_secondary: '#f9fafb',
  text_color_primary: '#111827',
  text_color_secondary: '#374151',
  text_color_light: '#6b7280',
  heading_font_family: 'Inter',
  body_font_family: 'Inter'
};

// Types for database-driven configuration
export interface CemeteryBranding {
  primary_color: string;
  primary_color_hover: string;
  secondary_color_1: string;
  secondary_color_2: string;
  secondary_color_3: string;
  secondary_color_4: string;
  secondary_color_5: string;
  secondary_color_6: string;
  accent_color: string;
  background_color_primary: string;
  background_color_secondary: string;
  text_color_primary: string;
  text_color_secondary: string;
  text_color_light: string;
  heading_font_family: string;
  body_font_family: string;
  heading_font_url?: string;
  body_font_url?: string;
  logo_url?: string;
  favicon_url?: string;
}

export interface CemeteryFeature {
  id: string;
  feature_key: string;
  custom_title: string;
  custom_description: string;
  icon_name: string;
  display_order: number;
  is_enabled: boolean;
  background_color_index: number;
  background_image_url?: string;
}

export interface WelcomeScreenConfig {
  welcome_heading: string;
  welcome_subheading: string;
  get_started_button_text: string;
  cards_per_row_desktop: number;
  cards_per_row_tablet: number;
  cards_per_row_mobile: number;
  show_logo: boolean;
  logo_height: string;
}

export interface CemeteryUIConfig {
  theme: 'light' | 'dark' | 'auto';
  border_radius: string;
  button_style: 'rounded' | 'square' | 'pill';
  card_style: 'flat' | 'elevated' | 'outlined';
  card_shadow?: string;
  spacing_unit: string;
  header_height: string;
  navigation_style: 'bottom' | 'top' | 'sidebar';
  glassmorphism_enabled: boolean;
  glassmorphism_opacity: number;
  animation_speed: 'slow' | 'normal' | 'fast';
}

export interface CemeteryTheme {
  chat_dark_mode?: boolean;
  chat_bg_color?: string;
  chat_input_bg_color?: string;
  chat_bubble_user_bg?: string;
  chat_bubble_assistant_bg?: string;
  chat_text_primary?: string;
  chat_text_secondary?: string;
  chat_border_color?: string;
  dark_mode_enabled?: boolean;
}

// Hook for cemetery branding
export const useCemeteryBranding = (enabled: boolean = true) => {
  const { tenant } = useTenant();
  const [data, setData] = useState<CemeteryBranding | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled) {
      setIsLoading(false);
      return;
    }

    const fetchBranding = async () => {
      try {
        console.log('🔍 useCemeteryBranding: Fetching branding for tenant:', tenant?.id);
        setIsLoading(true);
        setError(null);
        
        if (!tenant?.id || !isSupabaseAvailable()) {
          console.log('⏳ Waiting for tenant ID or Supabase to be available');
          setIsLoading(false);
          return;
        }

        // Use default branding for admin tenant
        if (tenant.id === 'admin') {
          console.log('✅ Using default branding for admin tenant');
          setData(defaultCemeteryBranding);
          setIsLoading(false);
          return;
        }

        // Fetch theme data from cemetery_themes
        const { data: themeData, error: themeError } = await supabase!
          .from('cemetery_themes')
          .select('*')
          .eq('cemetery_id', tenant.id)
          .single();

        // Fetch asset data from cemetery_assets
        const { data: assetData, error: assetError } = await supabase!
          .from('cemetery_assets')
          .select('logo_url, favicon_url')
          .eq('cemetery_id', tenant.id)
          .single();

        // Handle errors from both queries
        if (themeError && themeError.code !== 'PGRST116') {
          console.error('Failed to fetch theme data:', themeError);
          throw themeError;
        }

        if (assetError && assetError.code !== 'PGRST116') {
          console.error('Failed to fetch asset data:', assetError);
          throw assetError;
        }

        // Combine data from both tables
        const combinedBranding: CemeteryBranding = {
          ...(themeData || defaultCemeteryBranding),
          logo_url: assetData?.logo_url,
          favicon_url: assetData?.favicon_url
        };

        console.log('✅ Found cemetery branding:', combinedBranding);
        setData(combinedBranding);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to fetch branding';
        console.error('❌ useCemeteryBranding error:', errorMessage);
        // Fall back to default branding instead of showing error
        console.log('Using default branding as fallback');
        setData(defaultCemeteryBranding);
        setError(null);
      } finally {
        setIsLoading(false);
      }
    };

    fetchBranding();
  }, [tenant?.id, enabled]);

  return { data, isLoading, error };
};

// Hook for cemetery UI configuration
export const useCemeteryUIConfig = (enabled: boolean = true) => {
  const { tenant } = useTenant();
  const [data, setData] = useState<CemeteryUIConfig | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Default UI configuration fallback
  const defaultCemeteryUIConfig: CemeteryUIConfig = {
    theme: 'light',
    border_radius: '8px',
    button_style: 'rounded',
    card_style: 'elevated',
    card_shadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
    spacing_unit: '16px',
    header_height: '60px',
    navigation_style: 'bottom',
    glassmorphism_enabled: false,
    glassmorphism_opacity: 0.8,
    animation_speed: 'normal'
  };

  useEffect(() => {
    if (!enabled) {
      setData(defaultCemeteryUIConfig);
      setIsLoading(false);
      return;
    }

    const fetchUIConfig = async () => {
      try {
        console.log('🔍 useCemeteryUIConfig: Fetching UI config for tenant:', tenant?.id);
        setIsLoading(true);
        setError(null);
        
        // Early exit conditions - return default config
        if (!tenant?.id || tenant.id === 'admin' || !isSupabaseAvailable()) {
          console.log('⏳ Waiting for tenant ID or Supabase to be available');
          setData(defaultCemeteryUIConfig);
          setIsLoading(false);
          return;
        }

        try {
          // Fetch UI config from cemetery_themes table
          const { data: themeData, error: fetchError } = await supabase!
            .from('cemetery_themes')
            .select(`
              border_radius,
              button_style,
              card_style,
              card_shadow,
              spacing_unit,
              header_height,
              navigation_style,
              glassmorphism_enabled,
              glassmorphism_opacity,
              animation_speed,
              dark_mode_enabled
            `)
            .eq('cemetery_id', tenant.id)
            .single();

          if (fetchError) {
            console.warn('Failed to fetch UI config, using defaults:', fetchError);
            setData(defaultCemeteryUIConfig);
            setIsLoading(false);
            return;
          }

          if (themeData) {
            // Map the data to our interface
            const uiConfig: CemeteryUIConfig = {
              theme: themeData.dark_mode_enabled ? 'dark' : 'light',
              border_radius: themeData.border_radius || '8px',
              button_style: themeData.button_style || 'rounded',
              card_style: themeData.card_style || 'elevated',
              card_shadow: themeData.card_shadow || '0 1px 3px rgba(0, 0, 0, 0.1)',
              spacing_unit: themeData.spacing_unit || '16px',
              header_height: themeData.header_height || '60px',
              navigation_style: themeData.navigation_style || 'bottom',
              glassmorphism_enabled: themeData.glassmorphism_enabled || false,
              glassmorphism_opacity: themeData.glassmorphism_opacity || 0.8,
              animation_speed: themeData.animation_speed || 'normal'
            };
            
            console.log('✅ Found cemetery UI config:', uiConfig);
            setData(uiConfig);
          } else {
            console.warn('❌ No UI config data found, using defaults');
            setData(defaultCemeteryUIConfig);
          }
        } catch (fetchError) {
          console.warn('Supabase fetch failed, using default UI config:', fetchError);
          setData(defaultCemeteryUIConfig);
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to fetch UI config';
        console.warn('❌ useCemeteryUIConfig error, using defaults:', errorMessage);
        setData(defaultCemeteryUIConfig);
        setError(null); // Don't set error since we have fallback
      } finally {
        setIsLoading(false);
      }
    };

    fetchUIConfig();
  }, [tenant?.id, enabled]);

  return { data, isLoading, error };
};

// Hook for cemetery features - NO CHANGES NEEDED
export const useCemeteryFeatures = (enabled: boolean = true) => {
  const { tenant } = useTenant();
  const [data, setData] = useState<CemeteryFeature[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled) {
      setIsLoading(false);
      return;
    }

    const fetchFeatures = async () => {
      try {
        console.log('🔍 useCemeteryFeatures: Fetching features for tenant:', tenant?.id);
        setIsLoading(true);
        setError(null);
        
        if (!tenant?.id || !isSupabaseAvailable()) {
          console.log('⏳ Waiting for tenant ID or Supabase to be available');
          setIsLoading(false);
          return;
        }

        const { data: features, error: fetchError } = await supabase!
          .from('cemetery_features')
          .select('*')
          .eq('cemetery_id', tenant.id)
          .eq('is_enabled', true)
          .order('display_order');

        if (fetchError) {
          console.error('Failed to fetch features:', fetchError);
          throw fetchError;
        }

        if (features && features.length > 0) {
          console.log('✅ Found cemetery features:', features);
          // Debug: Log each feature's background image URL
          features.forEach((feature, index) => {
            console.log(`🖼️ Feature ${index + 1} (${feature.feature_key}):`, {
              title: feature.custom_title,
              background_image_url: feature.background_image_url,
              has_image: !!feature.background_image_url,
              url_type: typeof feature.background_image_url,
              url_length: feature.background_image_url?.length || 0,
              is_empty_string: feature.background_image_url === '',
              is_null: feature.background_image_url === null,
              is_undefined: feature.background_image_url === undefined,
              trimmed_url: feature.background_image_url?.trim()
            });
          });
          setData(features);
        } else {
          console.error('❌ No features data found for cemetery:', tenant.id);
          setError('No features data found');
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to fetch features';
        console.error('❌ useCemeteryFeatures error:', errorMessage);
        setError(errorMessage);
      } finally {
        setIsLoading(false);
      }
    };

    fetchFeatures();
  }, [tenant?.id, enabled]);

  return { data, isLoading, error };
};

// Hook for welcome screen configuration - UPDATED to use cemetery_themes
export const useWelcomeScreenConfig = (enabled: boolean = true) => {
  const { tenant } = useTenant();
  const [data, setData] = useState<WelcomeScreenConfig | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled) {
      setIsLoading(false);
      return;
    }

    const fetchWelcomeConfig = async () => {
      try {
        console.log('🔍 useWelcomeScreenConfig: Fetching welcome config for tenant:', tenant?.id);
        setIsLoading(true);
        setError(null);
        
        if (!tenant?.id || !isSupabaseAvailable()) {
          console.log('⏳ Waiting for tenant ID or Supabase to be available');
          setIsLoading(false);
          return;
        }

        // Now fetch from cemetery_themes instead of welcome_screen_config
        const { data: themeData, error: fetchError } = await supabase!
          .from('cemetery_themes')
          .select(`
            welcome_heading,
            welcome_subheading,
            get_started_button_text,
            show_logo,
            cards_per_row_desktop,
            cards_per_row_tablet,
            cards_per_row_mobile
          `)
          .eq('cemetery_id', tenant.id)
          .single();

        if (fetchError) {
          console.error('Failed to fetch welcome config:', fetchError);
          throw fetchError;
        }

        if (themeData) {
          // Map to WelcomeScreenConfig interface
          const welcomeConfig: WelcomeScreenConfig = {
            welcome_heading: themeData.welcome_heading || 'Welcome',
            welcome_subheading: themeData.welcome_subheading || '',
            get_started_button_text: themeData.get_started_button_text || 'Get Started',
            cards_per_row_desktop: themeData.cards_per_row_desktop || 4,
            cards_per_row_tablet: themeData.cards_per_row_tablet || 2,
            cards_per_row_mobile: themeData.cards_per_row_mobile || 1,
            show_logo: themeData.show_logo !== false,
            logo_height: '80px' // Default value, add to DB if needed
          };
          
          console.log('✅ Found welcome screen config:', welcomeConfig);
          setData(welcomeConfig);
        } else {
          console.error('❌ No welcome config data found for cemetery:', tenant.id);
          setError('No welcome config data found');
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to fetch welcome config';
        console.error('❌ useWelcomeScreenConfig error:', errorMessage);
        setError(errorMessage);
      } finally {
        setIsLoading(false);
      }
    };

    fetchWelcomeConfig();
  }, [tenant?.id, enabled]);

  return { data, isLoading, error };
};

// Hook for cemetery theme configuration (including chat dark mode)
export const useCemeteryTheme = (enabled: boolean = true) => {
  const { tenant } = useTenant();
  const [data, setData] = useState<CemeteryTheme | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled) {
      setIsLoading(false);
      return;
    }

    const fetchTheme = async () => {
      try {
        console.log('🔍 useCemeteryTheme: Fetching theme for tenant:', tenant?.id);
        setIsLoading(true);
        setError(null);
        
        if (!tenant?.id || !isSupabaseAvailable()) {
          console.log('⏳ Waiting for tenant ID or Supabase to be available');
          setIsLoading(false);
          return;
        }

        // Fetch theme data from cemetery_themes table
        const { data: themeData, error: fetchError } = await supabase!
          .from('cemetery_themes')
          .select(`
            chat_dark_mode,
            chat_bg_color,
            chat_input_bg_color,
            chat_bubble_user_bg,
            chat_bubble_assistant_bg,
            chat_text_primary,
            chat_text_secondary,
            chat_border_color,
            dark_mode_enabled
          `)
          .eq('cemetery_id', tenant.id)
          .single();

        if (fetchError && fetchError.code !== 'PGRST116') {
          console.error('Failed to fetch theme data:', fetchError);
          throw fetchError;
        }

        if (themeData) {
          console.log('✅ Found cemetery theme:', themeData);
          setData(themeData);
        } else {
          // Default theme settings
          console.log('Using default theme settings');
          setData({ 
            chat_dark_mode: true, 
            dark_mode_enabled: false,
            chat_bg_color: '#111827',
            chat_input_bg_color: '#1F2937',
            chat_bubble_user_bg: null, // Will use tenant primary color
            chat_bubble_assistant_bg: '#1F2937',
            chat_text_primary: '#F9FAFB',
            chat_text_secondary: '#D1D5DB',
            chat_border_color: '#374151'
          });
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to fetch theme';
        console.error('❌ useCemeteryTheme error:', errorMessage);
        // Use defaults on error
        setData({ 
          chat_dark_mode: true, 
          dark_mode_enabled: false,
          chat_bg_color: '#111827',
          chat_input_bg_color: '#1F2937',
          chat_bubble_user_bg: null,
          chat_bubble_assistant_bg: '#1F2937',
          chat_text_primary: '#F9FAFB',
          chat_text_secondary: '#D1D5DB',
          chat_border_color: '#374151'
        });
        setError(null);
      } finally {
        setIsLoading(false);
      }
    };

    fetchTheme();
  }, [tenant?.id, enabled]);

  return { data, isLoading, error };
};